// Copyright (c) 2025 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ValidationError } from '@/items/components/merge-item/hooks/use-merge-validation';
import { getFieldTypeArticle } from '@/items/components/merge-item/utilities/article-utilities';
import { ReactNode } from 'react';

/**
 * Builds a structured field mismatch error message with proper formatting
 */
function buildFieldMismatchMessage(
  fieldName: string,
  primaryItemName: string,
  selectedItemName: string,
  primaryFieldType: string,
  conflictingFieldType: string
): ReactNode {
  const primaryArticle = getFieldTypeArticle(primaryFieldType);
  const conflictingArticle = getFieldTypeArticle(conflictingFieldType);

  return (
    <>
      There is an item field mismatch. Both items have a field named &quot;
      <strong>{fieldName}</strong>&quot; but each has a different type.{' '}
      {primaryItemName} uses {primaryArticle}{' '}
      <strong>{primaryFieldType}</strong> field and {selectedItemName} uses{' '}
      {conflictingArticle} <strong>{conflictingFieldType}</strong> field. Please
      rename one of the fields before merging.
    </>
  );
}

interface Props {
  error: ValidationError;
}

/**
 * Default fallback error message for unknown or malformed errors
 */
const DEFAULT_ERROR_MESSAGE =
  'An error occurred during validation. Please try again.';

/**
 * Safely renders validation errors with proper fallback handling
 */
export function ValidationErrorDisplay(props: Props): ReactNode {
  const { error } = props;

  // Defensive check for malformed error object
  if (!error || typeof error !== 'object' || !error.type) {
    return DEFAULT_ERROR_MESSAGE;
  }

  try {
    switch (error.type) {
      case 'field_mismatch':
        if (
          !error.fieldName ||
          !error.primaryFieldType ||
          !error.conflictingFieldType ||
          !error.primaryItemName ||
          !error.selectedItemName
        ) {
          return 'There is an item field mismatch. Please check your field types and try again.';
        }

        return buildFieldMismatchMessage(
          error.fieldName,
          error.primaryItemName,
          error.selectedItemName,
          error.primaryFieldType,
          error.conflictingFieldType
        );

      case 'hubspot_conflict':
      case 'inventory_mismatch':
      case 'crm_conflict':
        // Ensure message exists and is a string
        if (error.message && typeof error.message === 'string') {
          return error.message;
        }
        return DEFAULT_ERROR_MESSAGE;

      default:
        // Handle unknown error types gracefully
        if (error.message && typeof error.message === 'string') {
          return error.message;
        }
        return DEFAULT_ERROR_MESSAGE;
    }
  } catch (renderError) {
    // Catch any rendering errors and provide fallback
    console.warn('Error rendering validation message:', renderError);
    return DEFAULT_ERROR_MESSAGE;
  }
}
